import * as fromState from './projects-custom-forms.state';
import * as fromActions from './projects-custom-forms.actions';
import { createReducer, on } from '@ngrx/store';
import { initialState } from '../projects.state';
import { CustomFormFieldSubmission } from '@core/objects/form';

const AW_LINK_V1_FIELD_TYPE = 'artwork-link-selector-v1';
const FILES_V1_FIELD_TYPE = 'files-v1';

// en each action variable type received type is set on projects-custom-forms.actions
export const projectsCustomFormsReducer = createReducer(
    fromState.initialState,
    /**
     * (state, { forms }): Here, the reducer receives two parameters: the current state and the action payload.
     * payload should match the shape of the props<{ forms: Form[] }> or other defined in each action.
     * This is because the props function expects an object that matches the shape defined in the action creator.
     */
    on(fromActions.LoadForms, state => ({ ...state, loadingForms: true, errorForms: null })),
    on(fromActions.LoadFormsSuccess, (state, { forms }) => ({ ...state, forms, loadingForms: false, initialized: true })),
    on(fromActions.LoadFormsFailure, (state, { error }) => ({ ...state, errorForms: error, loadingForms: false })),
    on(fromActions.LoadFieldSubmissions, (state, { formId }) => ({
        ...state,
        loadingFields: { ...state.loadingFields, [formId]: true },
        errorFields: { ...state.errorFields, [formId]: null }
    })),
    on(fromActions.LoadFieldSubmissionsSuccess, (state, { formId, fields }) => {
        // Check if formId exists in the state of loaded form fields
        const existingForm = state.formsFields.find(ff => ff.form_id === formId);

        // If the form exists, update its fields, otherwise add a new form fields item
        const updatedFormsFields = existingForm
        ? state.formsFields.map((ff: fromState.CustomFormsFieldsAvailable) =>
            ff.form_id === formId ? { ...ff, fields } : ff
            )
        : [...state.formsFields, { form_id: formId, fields }];

        return {...state,
            formsFields: updatedFormsFields,
            loadingFields: { ...state.loadingFields, [formId]: false },
        };
        
    }),
    on(fromActions.LoadFieldSubmissionsFailure, (state, { formId, error }) => ({
        ...state,
        errorFields: { ...state.errorFields, [formId]: error },
        loadingFields: { ...state.loadingFields, [formId]: false }
    })),
    // on(fromActions.LoadFieldsValuesSuccess, (state, { values }) => ({
    //     ...state,
    //     fieldValues: values
    //   })),
    on(fromActions.SetFieldValue, (state, { formId,  value }) => {

        const existingFormValue = state.fieldValues.find(ffv => ffv.form_id === formId);
        // If the form exists in field values, update its field values, otherwise add a new form field value
        const updatedFieldValues = existingFormValue
            ? state.fieldValues.map((ffv) =>
                ffv.form_id === formId
                    ? {
                        ...ffv,
                        field_values: ffv.field_values.some(fv => fv.uuid === value.uuid)
                            ? ffv.field_values.map(fv => fv.uuid === value.uuid ? value : fv) // Update existing field
                            : [...ffv.field_values, value] // Add new field if it doesn't exist
                    }
                    : ffv
            )
            : [...state.fieldValues, { form_id: formId, field_values: [value] }]; // Add new form values set if no form values set yet

        // If the form exists, update its fields, otherwise add a new form fields item
        const existingFormFields = state.formsFields.find(ff => ff.form_id === formId);
        const updatedFormsFields = (existingFormFields && (value.value === null && value.type && value.type === AW_LINK_V1_FIELD_TYPE )) 
            ? state.formsFields.map((ff: fromState.CustomFormsFieldsAvailable) =>
                ff.form_id === formId ? { ...ff, 
                    fields: ff.fields.some(f => f.field.uuid === value.uuid)
                            ? ff.fields.map(ffi => ffi.field.uuid === value.uuid ? {...ffi, serialized_value: value.value} : ffi) // Update existing field
                            : ff.fields // Add new field if it doesn't exist } : ff
                } : ff)
            : [...state.formsFields];
        
        return {
            ...state,
            fieldValues: updatedFieldValues,
            formsFields: updatedFormsFields
        };
    }),
    on(fromActions.SubmitFieldsValues, (state, { formId }) => {
        // Create a copy of errorForms without the formId key
        const updatedErrorFields = { ...state.errorFields };
        delete updatedErrorFields[formId];  // Remove the formId key

        return {
            ...state,
            errorFields: updatedErrorFields  // Update the state with the modified errorForms
        };
    }),
    on(fromActions.SubmitFieldsValuesFailure, (state, { formId, errorResponse }) => {
        return {
            ...state,
            errorFields: { ...state.errorFields, [formId]: errorResponse }
        }
    }),
    on(fromActions.SubmitFieldsValuesSuccess, (state, { formId, response }) => {
        // Check if the response contains any field of type 'files-v1'
        const hasFilesV1Field = response.submissions.some((fs: CustomFormFieldSubmission) => fs.field.type === FILES_V1_FIELD_TYPE);

        // If no 'files-v1' field is found, simply return the current state (with fieldValues reset)
        if (!hasFilesV1Field) {
            return {
                ...state,
                fieldValues: fromState.initialState.fieldValues, // Reset the current field values
            };
        }
        // Find the form in formFields by formId
        const updatedFormFields = state.formsFields.map((formFieldsAvailable: fromState.CustomFormsFieldsAvailable) => {
            if (formFieldsAvailable.form_id !== formId) {
                return formFieldsAvailable; // No changes for other forms
            }

            // For the matching form, replace the fields with the new submissions
            const updatedFields = formFieldsAvailable.fields.map((field: CustomFormFieldSubmission) => {
                // Check if the current field in the form matches a field from the response
                const updatedField = response.submissions.find((fs: CustomFormFieldSubmission) => fs.field.uuid === field.field.uuid);
                
                // Replace the field if it's present in the response, otherwise keep the existing field
                return updatedField ? updatedField : field;
            });

            // Return the updated form with the replaced fields
            return {
                ...formFieldsAvailable,
                fields: updatedFields
            };
        });

        return {
            ...state,
            formsFields: updatedFormFields,  // Update formFields with the replaced fields
            fieldValues: fromState.initialState.fieldValues,  // Reset fieldValues
        };
    }),
    on(fromActions.ClearFormFailures, state => ({
        ...state,
        errorForms: fromState.initialState.errorForms ,
    })),
    on(fromActions.ClearFormFieldValues, state => ({
        ...state,
            fieldValues: fromState.initialState.fieldValues,
            formsFields: fromState.initialState.formsFields,
            loadingFields: fromState.initialState.loadingFields,
            errorFields: fromState.initialState.errorFields
    }))
    
    // TODO ==> fromActions.SubmitFieldsValuesSuccess
);

  /**
   * Why Use { forms } or similar?

    Clarity and Consistency: Using { forms } ensures consistency between 
    the action creator and the reducer. The payload is always an object with 
    properties defined in props, making it clear what data is being passed around.

    Destructuring Convenience: Destructuring in the reducer (or in any function) 
    is a convenient way to access specific properties of an object. 
    Instead of accessing the payload and then extracting forms, 
    you can directly access forms from the destructured payload.

    Action Payload Shape: The action creator specifies the shape of the action payload. 
    By using { forms } in both the action creator and the reducer, 
    you ensure that the payload shape is adhered to, 
    and the reducer correctly handles the action.
   */